import { getIdToken, signInWithCustomToken, signOut } from "firebase/auth"

import { updateUserStateFromBackend } from "@fuse/recoil/signup"

import { auth } from "@firebaseConfig/clientApp"

import { getUserInfo, updateEmailAPI } from "@api/firebaseWrappedApis"

import { isDev } from "@fuse/utils/DeviceDection"

// Types for the service
export interface EmailVerificationConfig {
  email: string
  updateUserState: (updater: (prev: any) => any) => void
  showToast: {
    success: (message: string) => void
    error: (message: string) => void
  }
  onSignOutRequired?: () => void
  onLoginRequired?: () => void
}

export interface EmailVerificationResult {
  success: boolean
  isDevMode?: boolean
  requiresInboxCheck?: boolean
  error?: string
}

/**
 * Core email verification service - pure function with no React dependencies
 * Handles both development (fake verification) and production (real verification) flows
 */
export const emailVerificationCore = async ({
  email,
  updateUserState,
  showToast,
  onSignOutRequired,
  onLoginRequired,
}: EmailVerificationConfig): Promise<EmailVerificationResult> => {
  try {
    const currentUser = auth.currentUser
    if (!currentUser) {
      throw new Error("User not authenticated")
    }

    // Refresh auth token before making API call
    await getIdToken(currentUser, true)

    // Call the real API to save email to database
    const result = await updateEmailAPI({ email })

    // Check if API call was successful
    if (!result?.success) {
      const message = result?.message || "Failed to update email"
      showToast.error(`Error: ${message}`)
      return { success: false, error: message }
    }

    // Handle custom token sign-in (required after email update)
    if (result?.token) {
      try {
        const signInResult = await signInWithCustomToken(auth, result.token)

        // Update user info from backend
        const userInfo = (await getUserInfo({})) as any
        const currentUserInfo = userInfo?.userInfo

        if (currentUserInfo) {
          updateUserState((prev) => ({
            ...prev,
            ...currentUserInfo,
            uid: signInResult?.user?.uid,
            firstName: currentUserInfo.displayName?.split(" ")[0] || "",
            lastName: currentUserInfo.displayName?.split(" ")[1] || "",
            isOnboarded: true,
            isLoggedIn: true,
            email: email,
          }))
        }
      } catch (error) {
        console.error("Error signing in with custom token:", error)
        signOut(auth)
        onSignOutRequired?.()
        return { success: false, error: "Authentication failed" }
      }
    }

    const enableDevMode = false

    if (isDev() && enableDevMode) {
      // 🔥 DEV-ONLY: Fake verification flow
      console.log(
        "🚀 DEV MODE: Fake email verification - but still saving to DB!",
      )

      // Store fake verification in localStorage
      localStorage.setItem(`dev_email_verified_${currentUser.uid}`, "true")
      localStorage.setItem(`dev_verified_email_${currentUser.uid}`, email)

      // Update user state immediately (fake verification)
      updateUserState((prev) => ({
        ...prev,
        email: email,
        emailVerified: true,
      }))

      showToast.success("✅ DEV: Email saved to DB and fake verified!")

      return {
        success: true,
        isDevMode: true,
        requiresInboxCheck: false,
      }
    } else {
      // 🔥 REAL FLOW: Production verification process
      updateUserState((prev) => ({
        ...prev,
        email: email,
      }))

      showToast.success("Verification email sent! Please check your inbox.")

      return {
        success: true,
        isDevMode: false,
        requiresInboxCheck: true,
      }
    }
  } catch (error: any) {
    console.error("Error in emailVerificationCore:", error)
    const errorMessage = error?.message || "Failed to send verification email"
    if (
      errorMessage.includes("User not authenticated") ||
      errorMessage.includes("User authentication is required")
    ) {
      onLoginRequired?.()
    } else {
      showToast.error(`Error: ${errorMessage}`)
    }

    return {
      success: false,
      error: errorMessage,
    }
  }
}

/**
 * Helper function to start verification state checking
 * Used in production flow to poll for email verification completion
 */
export const startVerificationPolling = (
  onVerified: () => void,
  maxDuration: number = 60000 * 10, // 10 minutes
  pollInterval: number = 4000, // 4 seconds
): (() => void) => {
  const intervalId = setInterval(async () => {
    if (auth.currentUser) {
      await auth.currentUser.reload()
      if (auth.currentUser.emailVerified) {
        clearInterval(intervalId)
        await updateUserStateFromBackend()
        onVerified()
      }
    }
  }, pollInterval)

  // Auto-cleanup after max duration
  const timeoutId = setTimeout(() => {
    clearInterval(intervalId)
  }, maxDuration)

  // Return cleanup function
  return () => {
    clearInterval(intervalId)
    clearTimeout(timeoutId)
  }
}
